// import React, { useEffect, useState } from "react";
// import ReactMarkdown from "react-markdown";
// import { useParams, Link } from "react-router-dom";
// import Header from "../../components/header/Header";
// import { blogs, moreBlogs } from "../../data/blogData";
// import { loadBlog, getAllBlogs, getBlogBySlug } from "../../utils/loadBlogs";
// import { Helmet } from "react-helmet";
// import remarkGfm from "remark-gfm";
// import "./SingleBlogPage.css";
// import BackToTop from "../../components/backToTop/BackToTop";

// const SingleBlogPage = () => {
//   const { id } = useParams();
//   const { slug } = useParams();
//   const [blog, setBlog] = useState(null);

//   const [blogs, setBlogs] = useState([]);
//   const [related, setRelated] = useState([]);

//   useEffect(() => {
//     getBlogBySlug(slug).then(setBlog);
//   }, [slug]);

//   useEffect(() => {
//     getAllBlogs().then((all) => {
//       if (blog) {
//         const filtered = all.filter(
//           (b) => b.category === blog.category && b.slug !== slug,
//         );
//         setRelated(filtered.slice(0, 3));
//       }
//     });
//   }, [blog, slug]);

//   const allBlogs = [...blogs, ...moreBlogs];

//   if (!blog) return <div>Loading blog...</div>;

//   return (
//     <div className="singleblog-page">
//       <Header />

//       {/* HERO SECTION */}
//       <Helmet>
//         <title>{blog.title}</title>
//         <meta name="description" content={blog.content.slice(0, 150)} />
//       </Helmet>

//       <section className="singleblog-hero-section">
//         <div className="singleblog-container">
//           <Link to="/blog" className="singleblog-back">
//             ← Back to Blog
//           </Link>
//           <h1 className="singleblog-title">{blog.title}</h1>
//         </div>

//         {/* FULL WIDTH IMAGE */}

//         <div className="singleblog-hero-image">
//           <img src={blog.image} alt={blog.title} />
//         </div>
//       </section>

//       {/* CONTENT SECTION */}

//       <section className="singleblog-content-section">
//         <div className="singleblog-container">
//           <h1 className="singleblog-title">{blog.title}</h1>

//           <div className="singleblog-meta">
//             <span className="blog-category">{blog.category}</span>
//             <span className="singleblog-dot"></span>
//             <span>{blog.date}</span>
//             <span className="singleblog-dot"></span>
//             <span>{blog.time}</span>
//           </div>

//           <div className="singleblog-content">
//             <ReactMarkdown remarkPlugins={[remarkGfm]}>
//               {blog.content}
//             </ReactMarkdown>{" "}
//           </div>
//           <h3>Related Articles</h3>
//           {related.map((post) => (
//             <div key={post.slug}>
//               <a href={`/blog/${post.slug}`}>{post.title}</a>
//             </div>
//           ))}
//         </div>
//       </section>
//       <BackToTop />
//     </div>
//   );
// };

// export default SingleBlogPage;


import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import remarkGfm from "remark-gfm";
import Header from "../../components/header/Header";
import BackToTop from "../../components/backToTop/BackToTop";
import { getBlogBySlug, getAllBlogs } from "../../utils/loadBlogs";
import "./SingleBlogPage.css";

const SingleBlogPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load the main blog
  useEffect(() => {
    setLoading(true);
    setError(null);
    getBlogBySlug(slug)
      .then(setBlog)
      .catch(() => setError("Blog not found."))
      .finally(() => setLoading(false));
  }, [slug]);

  // Load related blogs once main blog is ready
  useEffect(() => {
    if (!blog) return;
    getAllBlogs().then((all) => {
      const filtered = all.filter(
        (b) => b.category === blog.category && b.slug !== slug
      );
      setRelated(filtered.slice(0, 3));
    });
  }, [blog, slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) return <div className="singleblog-loading">Loading...</div>;
  if (error) return <div className="singleblog-error">{error}</div>;

  return (
    <div className="singleblog-page">
      <Header />

      <Helmet>
        <title>{`${blog.title} | TutorsLinks Blog`}</title>
        <meta
          name="description"
          content={blog.excerpt || blog.content?.slice(0, 150)}
        />
        {/* Use blog.slug (the canonical field from the API), not the raw
            URL param — the backend also accepts a raw _id as a fallback,
            so this keeps both forms pointing at one indexable URL. */}
        <link
          rel="canonical"
          href={`https://www.tutorslinks.com/blog/${blog.slug}`}
        />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta
          property="og:description"
          content={blog.excerpt || blog.content?.slice(0, 150)}
        />
        <meta
          property="og:url"
          content={`https://www.tutorslinks.com/blog/${blog.slug}`}
        />
        {blog.coverImage && <meta property="og:image" content={blog.coverImage} />}
        {blog.date && <meta property="article:published_time" content={blog.date} />}
        {blog.category && <meta property="article:section" content={blog.category} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta
          name="twitter:description"
          content={blog.excerpt || blog.content?.slice(0, 150)}
        />
        {blog.coverImage && <meta name="twitter:image" content={blog.coverImage} />}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: blog.title,
            description: blog.excerpt || blog.content?.slice(0, 150),
            image: blog.coverImage ? [blog.coverImage] : undefined,
            datePublished: blog.date,
            dateModified: blog.updatedAt || blog.date,
            author: blog.author
              ? { "@type": "Person", name: blog.author }
              : { "@type": "Organization", name: "TutorsLinks" },
            publisher: {
              "@type": "Organization",
              name: "TutorsLinks",
              logo: {
                "@type": "ImageObject",
                url: "https://www.tutorslinks.com/images/tutorslinks.png",
              },
            },
            mainEntityOfPage: `https://www.tutorslinks.com/blog/${blog.slug}`,
          })}
        </script>
      </Helmet>

      {/* HERO SECTION */}
      <section className="singleblog-hero-section">
        <div className="singleblog-container">
          <Link to="/blog" className="singleblog-back">
            ← Back to Blog
          </Link>
          <h1 className="singleblog-title">{blog.title}</h1>
        </div>

        {/* COVER IMAGE — only render if present */}
        {blog.coverImage && (
          <div className="singleblog-hero-image">
            <img
              src={blog.coverImage}
              alt={blog.title}
              width={1200}
              height={630}
              loading="eager"
            />
          </div>
        )}
      </section>

      {/* CONTENT SECTION */}
      <section className="singleblog-content-section">
        <div className="singleblog-container">

          {/* META */}
          <div className="singleblog-meta">
            <span className="blog-category">{blog.category}</span>
            <span className="singleblog-dot"></span>
            <span>{formatDate(blog.date)}</span>
            <span className="singleblog-dot"></span>
            <span>{blog.readingTime}</span>
            {blog.author && (
              <>
                <span className="singleblog-dot"></span>
                <span>By {blog.author}</span>
              </>
            )}
          </div>

          {/* MARKDOWN CONTENT */}
          <div className="singleblog-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {blog.content}
            </ReactMarkdown>
          </div>

          {/* TAGS */}
          {blog.tags?.length > 0 && (
            <div className="singleblog-tags">
              {blog.tags.map((tag) => (
                <span key={tag} className="singleblog-tag">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* RELATED ARTICLES */}
          {related.length > 0 && (
            <div className="singleblog-related">
              <h3>Related Articles</h3>
              <div className="related-grid">
                {related.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="related-card"
                  >
                    {post.coverImage && (
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        width={300}
                        height={180}
                        loading="lazy"
                      />
                    )}
                    <div className="related-card-body">
                      <span className="related-category">{post.category}</span>
                      <p className="related-title">{post.title}</p>
                      <span className="related-date">
                        {formatDate(post.date)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </div>
      </section>

      <BackToTop />
    </div>
  );
};

export default SingleBlogPage;